import { useState, useCallback, useMemo, type CSSProperties } from 'react';
import { NodeApi } from 'react-arborist';
import { ChevronRightIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { Text, Flex, IconButton, Badge } from '@radix-ui/themes';
import { SnippetIcon } from '@/v2/assets/icons/SnippetIcon';
import { CheckmarkBadgeIcon } from '@/v2/assets/icons/CheckmarkBadgeIcon';
import { NotVerifiedIcon } from '@/v2/assets/icons/NotVerifiedIcon';
import type { SnippetTreeNode } from '@/v2/entities/KnowledgeSnippet';
import {
  useDeleteSnippet,
  useUpdateCollectionName,
} from '@/v2/entities/KnowledgeSnippet/KnowledgeSnippet.api';
import { AddSnippetCollectionDropdown } from './AddSnippetCollectionDropdown';
import { CollectionActionsDropdown } from './CollectionActionsDropdown';
import { CollectionEditDropdown } from './CollectionEditDropdown';
import { DeleteCollectionDialog } from './DeleteCollectionDialog';
import { DeleteSnippetDialog } from './DeleteSnippetDialog';
import { EditCollectionDialog } from './EditCollectionDialog';
import styles from './CollectionsSidebar.module.scss';

interface CollectionTreeNodeProps {
  node: NodeApi<SnippetTreeNode>;
  style: CSSProperties;
  dragHandle?: (el: HTMLDivElement | null) => void;
  onSnippetSelect: (snippetId: string) => void;
  onOpenCreateSnippet?: (collectionPath: string) => void;
  onOpenCreateCollection?: (collectionPath: string) => void;
  allNodes?: SnippetTreeNode[];
}


export function CollectionTreeNode({
  node,
  style,
  dragHandle,
  onSnippetSelect,
  onOpenCreateSnippet,
  onOpenCreateCollection,
  allNodes = [],
}: CollectionTreeNodeProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snippetDeleteDialogOpen, setSnippetDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const deleteSnippetMutation = useDeleteSnippet();
  const updateCollectionNameMutation = useUpdateCollectionName();

  const isFolder = useMemo(() => node.data.type === 'folder', [node.data.type]);
  const hasChildren = useMemo(() => node.data.children !== undefined, [node.data.children]);
  const hasActualChildren = useMemo(
    () => node.data.children && node.data.children.length > 0,
    [node.data.children]
  );
  const isHeaderOrSubfolder = useMemo(
    () => isFolder && node.data.snippetCount !== undefined,
    [isFolder, node.data.snippetCount]
  );
  const showChevron = useMemo(
    () => isFolder && hasChildren,
    [isFolder, hasChildren]
  );

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasActualChildren) {
        node.toggle();
      }
    },
    [hasActualChildren, node]
  );

  const handleClick = useCallback(() => {
    if (node.data.type === 'file' && node.data.snippetId) {
      onSnippetSelect(node.data.snippetId);
    } else if (isFolder && hasChildren) {
      node.toggle();
    }
  }, [node, isFolder, hasChildren, onSnippetSelect]);

  const handleAddSnippet = useCallback(() => {
    if (!onOpenCreateSnippet) {
      return;
    }
    let path = node.data.path || '';
    if (node.data.type === 'folder') {
      path = node.data.id.endsWith('/') ? node.data.id.slice(0, -1) : node.data.id;
    }
    onOpenCreateSnippet(path);
  }, [node.data, onOpenCreateSnippet]);

  const handleAddCollection = useCallback(() => {
    if (!onOpenCreateCollection) {
      return;
    }
    let path = node.data.path || '';
    if (node.data.type === 'folder') {
      path = node.data.id.endsWith('/') ? node.data.id.slice(0, -1) : node.data.id;
    }
    onOpenCreateCollection(path);
  }, [node.data, onOpenCreateCollection]);

  const handleDeleteCollection = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDeleteCollection = useCallback(() => {
    // TODO: Implement collection deletion
    // Collection deletion is not yet supported by backend
  }, [node.data]);

  const handleDeleteSnippet = useCallback(() => {
    setSnippetDeleteDialogOpen(true);
  }, []);

  const handleConfirmDeleteSnippet = useCallback(async () => {
    if (!node.data.snippetId) {
      return;
    }
    await deleteSnippetMutation.mutateAsync(node.data.snippetId);
  }, [node.data.snippetId, deleteSnippetMutation]);

  const handleEditCollection = useCallback(() => {
    setEditDialogOpen(true);
  }, []);

  const handleUpdateCollectionName = useCallback(
    async (newName: string) => {
      if (!isFolder) {
        return;
      }
      let collectionPath = node.data.path || '';
      if (node.data.type === 'folder') {
        collectionPath = node.data.id.endsWith('/') ? node.data.id.slice(0, -1) : node.data.id;
      }
      await updateCollectionNameMutation.mutateAsync({
        collectionPath,
        newCollectionName: newName,
      });
    },
    [isFolder, node.data, updateCollectionNameMutation]
  );

  return (
    <>
      <Flex
        ref={dragHandle}
        style={style}
        className={`${styles.treeNode} ${node.state.isSelected ? styles.selected : ''} ${isHeaderOrSubfolder ? styles.header : ''}`}
        onClick={handleClick}
        align="center"
        justify="between"
        height={isHeaderOrSubfolder ? '48px' : '40px'}
        px="2"
      >
        <Flex align="center" gap="1" style={{ flex: 1, minWidth: 0 }}>
          <Flex align="center" justify="center">
            {showChevron && (
              <IconButton
                variant="ghost"
                size="1"
                onClick={handleToggle}
                disabled={!hasActualChildren}
                aria-label={node.isOpen ? 'Collapse' : 'Expand'}
                className={styles.treeNodeChevron}
              >
                {node.isOpen ? <ChevronDownIcon width={12} height={12} /> : <ChevronRightIcon width={12} height={12} />}
              </IconButton>
            )}
          </Flex>

          {isHeaderOrSubfolder && (
            <Flex align="center" justify="center">
              <SnippetIcon size={12} fill="currentColor" />
            </Flex>
          )}

          {node.data.type === 'file' && (
            <Flex align="center" justify="center">
              {node.data.isVerifiedVersionAvailable ? (
                <CheckmarkBadgeIcon size={12} fill="green" />
              ) : (
                <NotVerifiedIcon size={12} />
              )}
            </Flex>
          )}

          <Text size="2" color="gray" weight="medium" highContrast className={styles.treeNodeName}>
            {node.data.name}
          </Text>

          {node.data.type === 'file' && node.data.isInternal !== undefined && (
            <Badge 
              color={node.data.isInternal ? "blue" : "purple"} 
              variant="soft" 
              size="1"
            >
              <Text size="1" weight="medium">
                {node.data.isInternal ? "Internal" : "External"}
              </Text>
            </Badge>
          )}

          {/* Snippet count for headers and subfolders */}
          {isHeaderOrSubfolder && node.data.snippetCount !== undefined && (
            <Text size="1" color="gray" className={styles.treeNodeSnippetCount}>
              {node.data.snippetCount} Snippets
            </Text>
          )}
        </Flex>

        {/* Action buttons - shown on hover */}
        <Flex
          className={styles.treeNodeActions}
          onClick={(e) => e.stopPropagation()}
          gap="2"
          align="center"
        >
          {/* Plus button - only for folders */}
          {isFolder && (
            <AddSnippetCollectionDropdown
              onAddSnippet={handleAddSnippet}
              onAddCollection={handleAddCollection}
            />
          )}

          {/* Edit collection dropdown - only for folders */}
          {isFolder && (
            <CollectionEditDropdown
              onEditCollection={handleEditCollection}
            />
          )}

          {/* Vertical dots menu - for all items */}
          <CollectionActionsDropdown
            itemType={node.data.type}
            onDeleteCollection={isFolder ? handleDeleteCollection : undefined}
            onDeleteSnippet={node.data.type === 'file' ? handleDeleteSnippet : undefined}
          />
        </Flex>
      </Flex>

      {/* Dialogs */}
      <DeleteCollectionDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        collectionName={node.data.name}
        onDeleteCollection={handleConfirmDeleteCollection}
      />
      {node.data.type === 'file' && (
        <DeleteSnippetDialog
          open={snippetDeleteDialogOpen}
          onOpenChange={setSnippetDeleteDialogOpen}
          snippetTitle={node.data.name}
          onDeleteSnippet={handleConfirmDeleteSnippet}
        />
      )}
      {isFolder && (
        <EditCollectionDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          collectionName={node.data.name}
          collectionPath={node.data.id.endsWith('/') ? node.data.id.slice(0, -1) : node.data.id}
          onUpdateCollection={handleUpdateCollectionName}
        />
      )}
    </>
  );
}
